import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, {
  sales as salesApi,
  marketplace as marketplaceApi,
} from "../services/api";
import { formatDate, formatCurrency } from "../utils/dateUtils";
import {
  BackIcon,
  TrendingUpIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ShoppingBagIcon,
  ClipboardListIcon,
  DollarIcon,
} from "../components/Icons";

function SalesAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalProfit: 0,
    itemsSold: 0,
    avgSalePrice: 0,
    avgProfit: 0,
    activeListings: 0,
    profitMargin: 0,
  });
  const [topPlatforms, setTopPlatforms] = useState([]);
  const [recentSales, setRecentSales] = useState([]);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);

      // Period filter — the server treats an absent param as all time, and
      // older servers simply ignore it.
      const periodParams = period === "all" ? {} : { period };

      // Load stats, the full sales list for the period (platform totals need
      // every sale, not a recent window), and marketplace listing stats.
      const [statsRes, salesRes, listingStatsRes] = await Promise.all([
        api.get("/sales/stats", { params: periodParams }),
        salesApi.list({ limit: -1, ...periodParams }),
        marketplaceApi.stats(),
      ]);

      const statsData = statsRes.data;
      const salesData = salesRes.data;
      const listingStats = listingStatsRes.data.data || {};

      // Calculate additional stats
      const avgSalePrice =
        statsData.totalSales > 0
          ? statsData.totalRevenue / statsData.totalSales
          : 0;
      const avgProfit =
        statsData.totalSales > 0
          ? statsData.totalProfit / statsData.totalSales
          : 0;
      const profitMargin =
        statsData.totalRevenue > 0
          ? (statsData.totalProfit / statsData.totalRevenue) * 100
          : 0;

      setStats({
        totalRevenue: statsData.totalRevenue || 0,
        totalProfit: statsData.totalProfit || 0,
        itemsSold: statsData.totalSales || 0,
        avgSalePrice,
        avgProfit,
        activeListings: listingStats.active || 0,
        profitMargin,
      });

      // Calculate top platforms from sales data
      const platformMap = {};
      salesData.forEach((sale) => {
        const channel = sale.channel || "Direct";
        if (!platformMap[channel]) {
          platformMap[channel] = { name: channel, revenue: 0, sales: 0 };
        }
        platformMap[channel].revenue += sale.sale_price || 0;
        platformMap[channel].sales += 1;
      });
      const platforms = Object.values(platformMap)
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);
      setTopPlatforms(platforms);

      // Format recent sales
      const recent = salesData.slice(0, 5).map((sale) => ({
        id: sale.id,
        name: sale.entity_name || sale.snapshot?.name || "Unknown Item",
        platform: sale.channel || "Direct",
        date: formatDate(sale.sale_date),
        price: sale.sale_price,
        profit: sale.profit,
      }));
      setRecentSales(recent);
    } catch (err) {
      console.error("Failed to load analytics:", err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const formatPercent = (value) => {
    return `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color }) => (
    <div className="card" style={{ padding: "var(--spacing-lg)" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              textTransform: "uppercase",
              marginBottom: "var(--spacing-xs)",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: color || "var(--text-primary)",
            }}
          >
            {value}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                marginTop: "var(--spacing-xs)",
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "var(--radius-md)",
            background: "var(--bg-tertiary)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-secondary)",
          }}
        >
          <Icon size={20} />
        </div>
      </div>
      {trend !== undefined && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-xs)",
            marginTop: "var(--spacing-sm)",
            fontSize: "0.75rem",
            color:
              trend >= 0 ? "var(--accent-success)" : "var(--accent-danger)",
          }}
        >
          {trend >= 0 ? <ArrowUpIcon size={12} /> : <ArrowDownIcon size={12} />}
          {formatPercent(trend)} vs last period
        </div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">Sales Analytics</h1>
            <p className="header-subtitle">Track your sales performance</p>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Period Selector */}
        <div style={{ marginBottom: "var(--spacing-lg)" }}>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="input"
            style={{ width: "auto", minWidth: "150px" }}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="year">This year</option>
            <option value="all">All time</option>
          </select>
        </div>

        {loading ? (
          <div
            className="card text-center"
            style={{ padding: "var(--spacing-2xl)" }}
          >
            <div className="spinner"></div>
            <p style={{ marginTop: "var(--spacing-md)" }}>
              Loading analytics...
            </p>
          </div>
        ) : (
          <>
            {/* Main Stats Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "var(--spacing-md)",
                marginBottom: "var(--spacing-xl)",
              }}
            >
              <StatCard
                title="Total Revenue"
                value={formatCurrency(stats.totalRevenue)}
                icon={DollarIcon}
              />
              <StatCard
                title="Total Profit"
                value={formatCurrency(stats.totalProfit)}
                icon={TrendingUpIcon}
                color={
                  stats.totalProfit >= 0
                    ? "var(--accent-success)"
                    : "var(--accent-danger)"
                }
              />
              <StatCard
                title="Items Sold"
                value={stats.itemsSold}
                icon={ShoppingBagIcon}
              />
              <StatCard
                title="Active Listings"
                value={stats.activeListings}
                icon={ClipboardListIcon}
              />
            </div>

            {/* Secondary Stats */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "var(--spacing-md)",
                marginBottom: "var(--spacing-xl)",
              }}
            >
              <div className="card" style={{ padding: "var(--spacing-lg)" }}>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    marginBottom: "var(--spacing-xs)",
                  }}
                >
                  Avg Sale Price
                </div>
                <div style={{ fontSize: "1.25rem", fontWeight: "700" }}>
                  {formatCurrency(stats.avgSalePrice)}
                </div>
              </div>
              <div className="card" style={{ padding: "var(--spacing-lg)" }}>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    marginBottom: "var(--spacing-xs)",
                  }}
                >
                  Avg Profit
                </div>
                <div style={{ fontSize: "1.25rem", fontWeight: "700" }}>
                  {formatCurrency(stats.avgProfit)}
                </div>
              </div>
              <div className="card" style={{ padding: "var(--spacing-lg)" }}>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    marginBottom: "var(--spacing-xs)",
                  }}
                >
                  Profit Margin
                </div>
                <div
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "700",
                    color:
                      stats.profitMargin >= 0
                        ? "var(--accent-success)"
                        : "var(--accent-danger)",
                  }}
                >
                  {stats.profitMargin.toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Top Platforms & Recent Sales */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "var(--spacing-lg)",
              }}
            >
              {/* Top Platforms */}
              <div className="card" style={{ padding: "var(--spacing-lg)" }}>
                <h3
                  style={{
                    marginBottom: "var(--spacing-md)",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Top Platforms
                </h3>
                {topPlatforms.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "var(--spacing-xl)",
                      color: "var(--text-muted)",
                    }}
                  >
                    <p>No platform data yet</p>
                    <p style={{ fontSize: "0.875rem" }}>
                      Start selling to see platform performance
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "var(--spacing-sm)",
                    }}
                  >
                    {topPlatforms.map((platform, index) => (
                      <div
                        key={platform.name}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "var(--spacing-sm) 0",
                          borderBottom:
                            index < topPlatforms.length - 1
                              ? "1px solid var(--border-color)"
                              : "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--spacing-sm)",
                          }}
                        >
                          <span
                            style={{
                              width: "24px",
                              height: "24px",
                              borderRadius: "var(--radius-full)",
                              background: "var(--bg-tertiary)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                            }}
                          >
                            {index + 1}
                          </span>
                          <span style={{ fontWeight: 500 }}>
                            {platform.name}
                          </span>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: 600 }}>
                            {formatCurrency(platform.revenue)}
                          </div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--text-muted)",
                            }}
                          >
                            {platform.sales} sales
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Sales */}
              <div className="card" style={{ padding: "var(--spacing-lg)" }}>
                <h3
                  style={{
                    marginBottom: "var(--spacing-md)",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Recent Sales
                </h3>
                {recentSales.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "var(--spacing-xl)",
                      color: "var(--text-muted)",
                    }}
                  >
                    <p>No sales yet</p>
                    <p style={{ fontSize: "0.875rem" }}>
                      Your recent sales will appear here
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "var(--spacing-sm)",
                    }}
                  >
                    {recentSales.map((sale, index) => (
                      <div
                        key={sale.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "var(--spacing-sm) 0",
                          borderBottom:
                            index < recentSales.length - 1
                              ? "1px solid var(--border-color)"
                              : "none",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 500 }}>{sale.name}</div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--text-muted)",
                            }}
                          >
                            {sale.platform} • {sale.date}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: 600 }}>
                            {formatCurrency(sale.price)}
                          </div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color:
                                sale.profit >= 0
                                  ? "var(--accent-success)"
                                  : "var(--accent-danger)",
                            }}
                          >
                            {formatCurrency(sale.profit)} profit
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Empty State Hint */}
            {stats.itemsSold === 0 && (
              <div
                className="card text-center"
                style={{
                  padding: "var(--spacing-2xl)",
                  marginTop: "var(--spacing-lg)",
                }}
              >
                <div
                  style={{
                    fontSize: "4rem",
                    marginBottom: "var(--spacing-lg)",
                    opacity: 0.5,
                  }}
                >
                  <TrendingUpIcon size={64} />
                </div>
                <h3 style={{ marginBottom: "var(--spacing-sm)" }}>
                  No sales data yet
                </h3>
                <p
                  className="text-muted"
                  style={{ marginBottom: "var(--spacing-lg)" }}
                >
                  Start selling items to see your analytics and performance
                  metrics
                </p>
                <button
                  onClick={() => navigate("/sales/listed")}
                  className="btn btn-primary"
                >
                  View Listings
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default SalesAnalytics;
